import { Injectable, OnDestroy } from '@angular/core';
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import Symbol from '@arcgis/core/symbols/Symbol';
import { asapScheduler, Observable, share, subscribeOn } from 'rxjs';
import { defaultHighlightConfig } from '../../constants/highlight.constant';
import { MapSymbolService } from '../../services/map-symbol.service';
import { ObjectUtils } from '../../utils/object.utils';
import { EsriUtils } from '../../utils/esri.utils';

export interface PointHighlightConfigLike {
  size?: number;
  fillColor?: string;
}

export interface LineHighlightConfigLike {
  outlineWidth?: number;
  outlineColor?: string;
}

export interface PolygonHighlightConfigLike extends LineHighlightConfigLike {
  fillColor?: string;
}

export interface HighlightConfigGroupLike {
  point: PointHighlightConfigLike | __esri.Symbol;
  line: LineHighlightConfigLike | __esri.Symbol;
  polygon: PolygonHighlightConfigLike | __esri.Symbol;
  opacity?: number;
}

@Injectable()
export class HighlightService implements OnDestroy {
  private groupLayer: GroupLayer;
  private polygonLayer: GraphicsLayer;
  private lineLayer: GraphicsLayer;
  private pointLayer: GraphicsLayer;

  constructor(private mapSymbolService: MapSymbolService) {
    this.polygonLayer = new GraphicsLayer();
    this.lineLayer = new GraphicsLayer();
    this.pointLayer = new GraphicsLayer();
    this.groupLayer = new GroupLayer({
      layers: [this.polygonLayer, this.lineLayer, this.pointLayer],
    });
  }

  ngOnDestroy(): void {
    EsriUtils.destroyLayersRecursive(this.groupLayer);
  }

  setup(layersMixin: __esri.LayersMixin) {
    layersMixin.add(this.groupLayer);
  }

  highlight(
    target: __esri.Geometry | __esri.Geometry[],
    configOrSymbol?: Partial<HighlightConfigGroupLike> | __esri.Symbol
  ): Observable<any> {
    if (!configOrSymbol) {
      configOrSymbol = defaultHighlightConfig;
    } else {
      configOrSymbol =
        configOrSymbol instanceof Symbol
          ? configOrSymbol
          : Object.assign({}, defaultHighlightConfig, configOrSymbol);
    }

    const geometryList = (Array.isArray(target) ? target : [target]).filter(
      (x) => !!x
    );
    const polygonList = geometryList.filter((x) => x.type === 'polygon');
    const lineList = geometryList.filter((x) => x.type === 'polyline');
    const pointList = geometryList.filter(
      (x) => x.type === 'point' || x.type === 'multipoint'
    );

    const resolveSymbolFn = (
      cfgSymbol: any,
      attr: keyof HighlightConfigGroupLike,
      list: any[],
      callback: (cfg: any) => __esri.Symbol
    ) => {
      if (!list?.length) {
        return null;
      }

      if (cfgSymbol instanceof Symbol) {
        return cfgSymbol;
      }

      if (cfgSymbol[attr] instanceof Symbol) {
        return cfgSymbol[attr];
      }

      const cfg = ObjectUtils.merge(
        {},
        defaultHighlightConfig[attr],
        cfgSymbol[attr],
        {
          opacity: cfgSymbol.opacity,
        }
      );
      return callback(cfg);
    };

    const polygonSymbol = resolveSymbolFn(
      configOrSymbol,
      'polygon',
      polygonList,
      (cfg) => this.mapSymbolService.createPolygonSymbol(cfg)
    );
    const lineSymbol = resolveSymbolFn(
      configOrSymbol,
      'line',
      lineList,
      (cfg) => this.mapSymbolService.createLineSymbol(cfg)
    );
    const pointSymbol = resolveSymbolFn(
      configOrSymbol,
      'point',
      pointList,
      (cfg) => this.mapSymbolService.createPointSymbol(cfg)
    );

    return new Observable((subscriber) => {
      const addGraphicsRange = (
        layer: __esri.GraphicsLayer,
        list: __esri.Geometry[],
        symbol?: __esri.Symbol
      ) => {
        if (!list?.length) {
          return;
        }

        const graphicList = list.map((geometry) => {
          return new Graphic({
            geometry,
            symbol,
          });
        });

        layer.addMany(graphicList);
        subscriber.add(() => {
          layer.removeMany(graphicList);
        });
      };

      addGraphicsRange(this.polygonLayer, polygonList, polygonSymbol);
      addGraphicsRange(this.lineLayer, lineList, lineSymbol);
      addGraphicsRange(this.pointLayer, pointList, pointSymbol);
    }).pipe(subscribeOn(asapScheduler), share());
  }
}
